import React, { useState, useEffect, useCallback } from 'react';
import { audienceMemberApi, audienceApi, campaignApi, communicationApi } from '../../api';
import { toast } from 'react-toastify';

// Import marketing components
import MarketingStats from '../marketing/MarketingStats';
import MarketingTabs from '../marketing/MarketingTabs';
import AudienceMemberList from '../marketing/AudienceMemberList';
import AudienceMemberForm from '../marketing/AudienceMemberForm';
import AudienceList from '../marketing/AudienceList';
import AudienceForm from '../marketing/AudienceForm';
import CampaignList from '../marketing/CampaignList';
import CampaignForm from '../marketing/CampaignForm';
import BulkImportModal from '../marketing/BulkImportModal';
import AudienceMembersModal from '../marketing/AudienceMembersModal';
import CampaignAnalyticsModal from '../marketing/CampaignAnalyticsModal';
import MarketingReports from '../marketing/MarketingReports';
import MemberCommunicationModal from '../marketing/MemberCommunicationModal';
import FinancialDataSyncPanel from '../marketing/FinancialDataSyncPanel';

const Marketing = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('members');

  // Data states
  const [members, setMembers] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);

  // Loading states
  const [loading, setLoading] = useState(true);

  // Search states (immediate value for input display)
  const [memberSearch, setMemberSearch] = useState('');
  const [audienceSearch, setAudienceSearch] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');

  // Debounced search states (delayed value for API calls)
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');

  // Pagination states
  const [memberPage, setMemberPage] = useState(1);
  const [audiencePage, setAudiencePage] = useState(1);
  const [campaignPage, setCampaignPage] = useState(1);
  const pageSize = 10;

  // Debounce member search - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMemberSearch(memberSearch);
      // Reset to page 1 when search changes
      if (memberSearch !== debouncedMemberSearch) {
        setMemberPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [memberSearch]);

  // Modal states
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showAudienceMembers, setShowAudienceMembers] = useState(false);
  const [showCampaignAnalytics, setShowCampaignAnalytics] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);

  // Edit states
  const [editingMember, setEditingMember] = useState(null);
  const [editingAudience, setEditingAudience] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedMemberForCommunication, setSelectedMemberForCommunication] = useState(null);

  // All members for audience form (not paginated)
  const [allMembers, setAllMembers] = useState([]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    try {
      const data = await audienceMemberApi.getAllPaginated(memberPage, pageSize, debouncedMemberSearch);
      setMembers(data?.members || []);
      setTotalMembers(data?.total || 0);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to fetch audience members');
    }
  }, [memberPage, debouncedMemberSearch]);

  // Fetch audiences
  const fetchAudiences = useCallback(async () => {
    try {
      const data = await audienceApi.getAll(audiencePage, pageSize, audienceSearch);
      setAudiences(data || []);
    } catch (error) {
      console.error('Error fetching audiences:', error);
      toast.error('Failed to fetch audiences');
    }
  }, [audiencePage, audienceSearch]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await campaignApi.getAll(campaignPage, pageSize, campaignSearch);
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
    }
  }, [campaignPage, campaignSearch]);

  // Fetch all members for audience form (no pagination)
  const fetchAllMembersForAudienceForm = useCallback(async () => {
    try {
      // Fetch with a large page size to get all members
      const data = await audienceMemberApi.getAll(1, 10000, '');
      setAllMembers(data || []);
    } catch (error) {
      console.error('Error fetching all members:', error);
      toast.error('Failed to fetch members for audience selection');
    }
  }, []);

  // Initial data fetch - only runs once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [membersData, audiencesData, campaignsData] = await Promise.all([
          audienceMemberApi.getAllPaginated(1, pageSize, ''),
          audienceApi.getAll(1, pageSize, ''),
          campaignApi.getAll(1, pageSize, ''),
        ]);
        setMembers(membersData?.members || []);
        setTotalMembers(membersData?.total || 0);
        setAudiences(audiencesData || []);
        setCampaigns(campaignsData || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []); // Empty dependency - only runs once on mount

  // Fetch members when search or page changes (after initial load)
  useEffect(() => {
    // Skip if still in initial loading
    if (loading) return;
    fetchMembers();
  }, [debouncedMemberSearch, memberPage]);

  // Fetch audiences when search or page changes (after initial load)
  useEffect(() => {
    if (loading) return;
    fetchAudiences();
  }, [audienceSearch, audiencePage]);

  // Fetch campaigns when search or page changes (after initial load)
  useEffect(() => {
    if (loading) return;
    fetchCampaigns();
  }, [campaignSearch, campaignPage]);

  // Member handlers
  const handleAddMember = async (memberInfo) => {
    try {
      await audienceMemberApi.add(memberInfo);
      toast.success('Member added successfully');
      setShowMemberForm(false);
      fetchMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('Failed to add member');
    }
  };

  const handleUpdateMember = async (member) => {
    try {
      await audienceMemberApi.update(member);
      toast.success('Member updated successfully');
      setShowMemberForm(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update member');
    }
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      await audienceMemberApi.delete(id);
      toast.success('Member deleted successfully');
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete member');
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setShowMemberForm(true);
  };

  // Communication handlers
  const handleViewCommunications = (member) => {
    setSelectedMemberForCommunication(member);
    setShowCommunicationModal(true);
  };

  const handleCommunicationMemberUpdated = () => {
    // Refresh members list when a communication is added (status may have changed)
    fetchMembers();
  };

  // Audience handlers
  const handleAddAudience = async (audience) => {
    try {
      if (audience?.useFilters) {
        const filters = {
          ...(audience.filters || {}),
        };

        if (!filters.notInAudienceId && audience?.notInAudienceId) {
          filters.notInAudienceId = audience.notInAudienceId;
        }

        const audienceId = await audienceApi.addWithFilters(
          {
            name: audience.name,
            description: audience.description,
            channel: audience.channel,
            audienceMembers: [],
          },
          filters
        );
        toast.success(`Audience created from filters (${audienceId})`);
      } else {
        await audienceApi.add(audience);
        toast.success('Audience created successfully');
      }
      setShowAudienceForm(false);
      fetchAudiences();
    } catch (error) {
      console.error('Error adding audience:', error);
      toast.error('Failed to create audience');
    }
  };

  const handleUpdateAudience = async (audience) => {
    try {
      await audienceApi.update(audience);
      toast.success('Audience updated successfully');
      setShowAudienceForm(false);
      setEditingAudience(null);
      fetchAudiences();
    } catch (error) {
      console.error('Error updating audience:', error);
      toast.error('Failed to update audience');
    }
  };

  const handleDeleteAudience = async (id) => {
    if (!window.confirm('Are you sure you want to delete this audience?')) return;
    try {
      await audienceApi.delete(id);
      toast.success('Audience deleted successfully');
      fetchAudiences();
    } catch (error) {
      console.error('Error deleting audience:', error);
      toast.error('Failed to delete audience');
    }
  };

  const handleEditAudience = async (audience) => {
    await fetchAllMembersForAudienceForm();
    setEditingAudience(audience);
    setShowAudienceForm(true);
  };

  const handleCsvAudienceImport = async (file, name, description, channel, memberType = 'AUTO_SHOP') => {
    try {
      const result = await audienceApi.createFromCsv(file, name, description, channel, memberType);
      if (result.audienceId) {
        toast.success(`Audience "${name}" created with ${result.newMembersCreated + result.existingMembersUsed} members`);
        if (result.existingMembersUsed > 0) {
          toast.info(`${result.existingMembersUsed} existing members were added`);
        }
        if (result.newMembersCreated > 0) {
          toast.info(`${result.newMembersCreated} new members were created`);
        }
      }
      if (result.errors && result.errors.length > 0) {
        console.warn('CSV import errors:', result.errors);
        toast.warning(`${result.failedCount} rows failed to import`);
      }
      setShowAudienceForm(false);
      fetchAudiences();
      fetchMembers();
    } catch (error) {
      console.error('Error creating audience from CSV:', error);
      toast.error('Failed to create audience from CSV');
      throw error;
    }
  };

  const handleViewAudienceMembers = (audience) => {
    setSelectedAudience(audience);
    setShowAudienceMembers(true);
  };

  // Campaign handlers
  const handleAddCampaign = async (campaign) => {
    try {
      await campaignApi.add(campaign);
      toast.success('Campaign created successfully');
      setShowCampaignForm(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error adding campaign:', error);
      const message = typeof error.response?.data === 'string'
        ? error.response.data
        : (error.response?.data?.message || 'Failed to create campaign');
      toast.error(message);
      throw new Error(message);
    }
  };

  const handleUpdateCampaign = async (campaign) => {
    try {
      await campaignApi.update(campaign);
      toast.success('Campaign updated successfully');
      setShowCampaignForm(false);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating campaign:', error);
      const message = typeof error.response?.data === 'string'
        ? error.response.data
        : (error.response?.data?.message || 'Failed to update campaign');
      toast.error(message);
      throw new Error(message);
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await campaignApi.delete(id);
      toast.success('Campaign deleted successfully');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      const message = error.response?.data || 'Failed to delete campaign';
      toast.error(message);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowCampaignForm(true);
  };

  const handleViewCampaignAnalytics = (campaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignAnalytics(true);
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await campaignApi.pause(campaignId);
      toast.success('Campaign paused');
      fetchCampaigns();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      toast.error('Failed to pause campaign');
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      await campaignApi.resume(campaignId);
      toast.success('Campaign resumed');
      fetchCampaigns();
    } catch (error) {
      console.error('Error resuming campaign:', error);
      toast.error('Failed to resume campaign');
    }
  };

  const handleSendNow = async (campaignId) => {
    if (!window.confirm('Are you sure you want to start sending this campaign now?')) return;
    try {
      await campaignApi.startNow(campaignId);
      toast.success('Campaign started! Emails are being sent.');
      fetchCampaigns();
    } catch (error) {
      console.error('Error starting campaign:', error);
      const message = error.response?.data || 'Failed to start campaign';
      toast.error(message);
    }
  };

  // Bulk import handler
  const handleBulkImport = async (file, memberType = 'AUTO_SHOP') => {
    try {
      const result = await audienceMemberApi.bulkImport(file, memberType);
      toast.success(`Imported ${result.successCount} of ${result.totalRows} members`);
      if (result.errors && result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
      }
      // Don't close modal here - let user see results and close manually
      fetchMembers();
      return result;
    } catch (error) {
      console.error('Error bulk importing:', error);
      toast.error('Failed to import members');
      throw error;
    }
  };

  // Subscription handler
  const handleUpdateSubscription = async (memberId, audienceId, subscribed) => {
    try {
      await audienceMemberApi.updateSubscription(memberId, audienceId, subscribed);
      toast.success(`Member ${subscribed ? 'subscribed' : 'unsubscribed'} successfully`);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      {/* Statistics */}
      <MarketingStats
        members={members}
        audiences={audiences}
        campaigns={campaigns}
        totalMembers={totalMembers}
      />

      {/* Tabs */}
      <MarketingTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onAddMember={() => { setEditingMember(null); setShowMemberForm(true); }}
        onAddAudience={async () => { await fetchAllMembersForAudienceForm(); setEditingAudience(null); setShowAudienceForm(true); }}
        onAddCampaign={() => { setEditingCampaign(null); setShowCampaignForm(true); }}
        onBulkImport={() => setShowBulkImport(true)}
      />

      {/* Content based on active tab */}
      {activeTab === 'members' && (
        <>
          <FinancialDataSyncPanel />
          <AudienceMemberList
            members={members}
            searchTerm={memberSearch}
            setSearchTerm={setMemberSearch}
            onEdit={handleEditMember}
            onDelete={handleDeleteMember}
            onViewCommunications={handleViewCommunications}
            page={memberPage}
            setPage={setMemberPage}
            totalMembers={totalMembers}
            pageSize={pageSize}
          />
        </>
      )}

      {activeTab === 'audiences' && (
        <AudienceList
          audiences={audiences}
          searchTerm={audienceSearch}
          setSearchTerm={setAudienceSearch}
          onEdit={handleEditAudience}
          onDelete={handleDeleteAudience}
          onViewMembers={handleViewAudienceMembers}
          page={audiencePage}
          setPage={setAudiencePage}
        />
      )}

      {activeTab === 'campaigns' && (
        <CampaignList
          campaigns={campaigns}
          audiences={audiences}
          searchTerm={campaignSearch}
          setSearchTerm={setCampaignSearch}
          onEdit={handleEditCampaign}
          onDelete={handleDeleteCampaign}
          onViewAnalytics={handleViewCampaignAnalytics}
          onPause={handlePauseCampaign}
          onResume={handleResumeCampaign}
          onSendNow={handleSendNow}
          page={campaignPage}
          setPage={setCampaignPage}
        />
      )}

      {activeTab === 'reports' && (
        <MarketingReports />
      )}

      {/* Modals */}
      <AudienceMemberForm
        show={showMemberForm}
        member={editingMember}
        onSubmit={editingMember ? handleUpdateMember : handleAddMember}
        onClose={() => { setShowMemberForm(false); setEditingMember(null); }}
      />

      <AudienceForm
        show={showAudienceForm}
        audience={editingAudience}
        members={allMembers}
        onSubmit={editingAudience ? handleUpdateAudience : handleAddAudience}
        onCsvImport={handleCsvAudienceImport}
        onClose={() => { setShowAudienceForm(false); setEditingAudience(null); }}
      />

      <CampaignForm
        show={showCampaignForm}
        campaign={editingCampaign}
        audiences={audiences}
        onSubmit={editingCampaign ? handleUpdateCampaign : handleAddCampaign}
        onClose={() => { setShowCampaignForm(false); setEditingCampaign(null); }}
      />

      <BulkImportModal
        show={showBulkImport}
        onImport={handleBulkImport}
        onClose={() => setShowBulkImport(false)}
      />

      <AudienceMembersModal
        show={showAudienceMembers}
        audience={selectedAudience}
        onUpdateSubscription={handleUpdateSubscription}
        onClose={() => { setShowAudienceMembers(false); setSelectedAudience(null); }}
      />

      <CampaignAnalyticsModal
        show={showCampaignAnalytics}
        campaign={selectedCampaign}
        onClose={() => { setShowCampaignAnalytics(false); setSelectedCampaign(null); }}
      />

      <MemberCommunicationModal
        show={showCommunicationModal}
        member={selectedMemberForCommunication}
        onClose={() => { setShowCommunicationModal(false); setSelectedMemberForCommunication(null); }}
        onMemberUpdated={handleCommunicationMemberUpdated}
      />
    </div>
  );
};

export default Marketing;



